import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable } from "rxjs";
import { Profile } from "./../models/profile";
import { Course } from "./../models/course";

import { PortalApiService } from "./../../../shared/services/api/portal-api.service";
import { AppSettingsService } from "./../../../shared/services/app-settings.service";
import { PractitionerMocksService } from "./../../../shared/services/mocks/practitioner-mocks.service";
import { UserService } from "./../../../shared/services/user.service";
import { finalize } from "rxjs/operators";

const apiEndpoints = {
  profile: "practitioner/{id}/profile",
  demographics: "practitioner/{id}/demographics",
  discipline: "practitioner/{id}/discipline/{discipline}",
  badgeMgmt: "practitioner/{id}/badge",
  eligibleExams: "practitioner/{id}/eligibleExams",
  credentials: "practitioner/{id}/credentials",
  ccp: "ccp",
  course: "practitioner/{id}/courseCompletions",
  importCourse: "practitioner/{id}/ImportAARC/{aarcId}",
  deleteCourse: "practitioner/{id}/CourseCompletions/{courseCompletionID}",
  getCourse: "practitioner/{id}/CourseCompletions/{courseCompletionID}",
  purchaseHistory: "transactions/list",
  purchaseDetail: "transactions",
  purchaseDetailReceipt: "transactions/{id}/emailreceipt?email=",
  ceCourseReport: "ccp/{id}/CourseReport",
  certificates: "practitionerFile/getCerts/{personId}",
  certificateDoc: "practitionerFile/getCertFile/{docId}/{personId}",
};

@Injectable()
export class PractitionerService {
  constructor(
    private appSettings: AppSettingsService,
    private portalApiService: PortalApiService,
    private userService: UserService,
    private practitionerMocksService: PractitionerMocksService
  ) {}

  getProfile(id: string): Observable<any> {
    if (this.userService.isCurrentUserMock) {
      this.practitionerMocksService.profile;
    }

    return this.portalApiService.get(apiEndpoints.profile.replace("{id}", id));
  }

  getBadge(id: string): Observable<any> {
    return this.portalApiService.get(
      apiEndpoints.badgeMgmt.replace("{id}", id)
    );
  }

  updateProfile(id: string, profileData: Profile): Observable<any> {
    if (this.userService.isCurrentUserMock)
      {return new BehaviorSubject<any>(
        this.practitionerMocksService.updateProfile()
      );}

    return this.portalApiService.put(
      apiEndpoints.profile.replace("{id}", id),
      profileData
    );
  }

  getDemographics(id: string): Observable<any> {
    return this.portalApiService.get(
      apiEndpoints.demographics.replace("{id}", id)
    );
  }

  updateDemographics(id: string, profileData: any): Observable<any> {
    if (this.userService.isCurrentUserMock)
      {return new BehaviorSubject<any>(
        this.practitionerMocksService.updateDemographics()
      );}

    return this.portalApiService.put(
      apiEndpoints.demographics.replace("{id}", id),
      profileData
    );
  }

  updateDiscipline(id: string, discipline: any): Observable<any> {
    return this.portalApiService.post(
      apiEndpoints.discipline.replace("{id}", id).replace("{discipline}",discipline));
  }

  getCCPInfo(id: string): Observable<any> {
    if (this.userService.isCurrentUserMock) {
      return new BehaviorSubject<any>(
        this.practitionerMocksService.getCCPInfo()
      );
    }

    return this.portalApiService
      .get(apiEndpoints.ccp.concat(`/${id}`))
      .pipe(finalize(() => null));
  }

  ccpExecute(id: string): Observable<any> {
    if (this.userService.isCurrentUserMock) {
      return new BehaviorSubject<any>(
        this.practitionerMocksService.getCCPInfo()
      );
    }

    return this.portalApiService.post(apiEndpoints.ccp.concat(`/${id}`));
  }

  getCredentials(id: string): Observable<any> {
    if (this.userService.isCurrentUserMock)
      {return new BehaviorSubject<any>(
        this.practitionerMocksService.getCredentialsAndExams().credentials
      );}

    return this.portalApiService.get(
      apiEndpoints.credentials.replace("{id}", id)
    );
  }

  getEligibleExams(id: string): Observable<any> {
    if (this.userService.isCurrentUserMock)
      {return new BehaviorSubject<any>(
        this.practitionerMocksService.getCredentialsAndExams().eligibleExams
      );}

    return this.portalApiService.get(
      apiEndpoints.eligibleExams.replace("{id}", id)
    );
  }

  addCourse(id: string, courseData: Course): Observable<any> {
    if (this.userService.isCurrentUserMock) {
      return new BehaviorSubject<any>(
        this.practitionerMocksService.addCourse()
      );
    }

    return this.portalApiService.post(
      apiEndpoints.course.replace("{id}", id),
      courseData
    );
  }
  getCourse(personId: string, courseId: string): Observable<any> {
    return this.portalApiService.get(
      apiEndpoints.getCourse
        .replace("{id}", personId)
        .replace("{courseCompletionID}", courseId)
    );
  }

  updateCourse(id: string, courseData: Course): Observable<any> {
    if (this.userService.isCurrentUserMock) {
      return new BehaviorSubject<any>(
        this.practitionerMocksService.addCourse()
      );
    }

    return this.portalApiService.put(
      apiEndpoints.course.replace("{id}", id),
      courseData
    );
  }

  deleteCourse(practitionerId: string, courseId: string): Observable<any> {
    if (this.userService.isCurrentUserMock) {
      return new BehaviorSubject<any>(
        this.practitionerMocksService.deleteCourse()
      );
    }

    return this.portalApiService.delete(
      apiEndpoints.deleteCourse
        .replace("{id}", practitionerId)
        .replace("{courseCompletionID}", courseId)
    );
  }

  importCourse(id: string, aarcId: string): Observable<any> {
    if (this.userService.isCurrentUserMock) {
      return new BehaviorSubject<any>(
        this.practitionerMocksService.importCourse()
      );
    }

    return this.portalApiService.get(
      apiEndpoints.importCourse.replace("{id}", id).replace("{aarcId}", aarcId)
    );
  }

  courseCompletions(id: string): Observable<any> {
    if (this.userService.isCurrentUserMock) {
      return new BehaviorSubject<any>(this.practitionerMocksService.courses);
    }

    return this.portalApiService.get(apiEndpoints.course.replace("{id}", id));
  }

  getPurchaseHistory(id: string, monthsHistory: number): Observable<any> {
    return this.portalApiService
      .get(
        apiEndpoints.purchaseHistory.concat(
          `/${id}?monthsHistory=${monthsHistory}`
        )
      )
      .pipe(finalize(() => null));
  }

  getPurchaseDetail(purchaseId: string): Observable<any> {
    if (this.userService.isCurrentUserMock) {
      return new BehaviorSubject<any>(
        this.practitionerMocksService.getPurchaseDetail()
      );
    }

    return this.portalApiService
      .get(apiEndpoints.purchaseDetail.concat(`/${purchaseId}`))
      .pipe(finalize(() => null));
  }

  getEmailPurchaseDetail(purchaseId: string, email: string): Observable<any> {
    return this.portalApiService
      .get(
        apiEndpoints.purchaseDetailReceipt
          .replace("{id}", purchaseId)
          .concat(`${email}`)
      )
      .pipe(finalize(() => true));
  }

  generateCEReport(id: any): Observable<any> {
    return this.portalApiService.post(
      apiEndpoints.ceCourseReport.replace("{id}", id),
      null,
      true
    );
  }

  getMyDocumentsCertificates(personId: string): Observable<any> {
    return this.portalApiService
      .get(apiEndpoints.certificates.replace("{personId}", personId))
      .pipe(finalize(() => true));
  }

  getMyDocumentCertificate(id: string, personId: string): Observable<any> {
    return this.portalApiService.getBlob(
      apiEndpoints.certificateDoc
        .replace("{personId}", personId)
        .replace("{docId}", id)
    );
  }
}
